import { useState } from 'react';
import { MessageSquarePlus, Trash2, Loader2 } from 'lucide-react';
import apiClient from '@/services/api/apiClient';

export interface StageRemark { note: string; by: string; at: string; }

// Manual remarks for a single ABM journey stage. Persists to
// stageData._stageRemarks[n] via the journey stage-remark endpoints.
export default function StageRemarks({ journeyId, stageN, initial = [] }: {
  journeyId?: string; stageN: number; initial?: StageRemark[];
}) {
  const [remarks, setRemarks] = useState<StageRemark[]>(initial);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!note.trim() || !journeyId) return;
    setSaving(true);
    try {
      const { data } = await apiClient.post(`/api/abm-journeys/${journeyId}/stages/${stageN}/remark`, { note: note.trim() });
      setRemarks(data.remarks || []);
      setNote('');
    } finally { setSaving(false); }
  };
  const remove = async (i: number) => {
    if (!journeyId) return;
    const { data } = await apiClient.delete(`/api/abm-journeys/${journeyId}/stages/${stageN}/remark/${i}`);
    setRemarks(data.remarks || []);
  };
  const fmt = (iso: string) => { try { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

  return (
    <div className="mt-3 rounded-lg border border-border p-3 space-y-2">
      <p className="text-xs font-semibold flex items-center gap-1.5"><MessageSquarePlus className="h-3.5 w-3.5 text-primary" /> Remarks</p>
      {remarks.length > 0 && (
        <div className="space-y-1">
          {remarks.map((r, i) => (
            <div key={i} className="flex items-start gap-2 text-xs bg-muted/40 rounded px-2 py-1">
              <div className="flex-1 min-w-0"><span className="text-foreground/90">{r.note}</span><span className="text-muted-foreground"> — {r.by || 'user'}, {fmt(r.at)}</span></div>
              <button onClick={() => remove(i)} className="text-muted-foreground hover:text-red-500 shrink-0" title="Delete"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
      {journeyId ? (
        <div className="flex gap-1.5">
          <input value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }}
            placeholder="Add a remark for this stage…" className="flex-1 text-xs px-2 py-1 rounded border border-border bg-background" />
          <button onClick={add} disabled={saving || !note.trim()} className="text-xs px-2.5 py-1 rounded bg-primary text-primary-foreground disabled:opacity-50 flex items-center">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
          </button>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">Save the journey first to add remarks.</p>
      )}
    </div>
  );
}
